'use client'

import useI18n from 'Hooks/useI18n'
import { CodeBlock, ScrollableBlock } from './GuideShared'
import GuideModalShell from './GuideModalShell'

type LayoutGuideModalProps = {
  open: boolean
  onClose: () => void
}

function Page1() {
  const { t } = useI18n()
  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('What is CSS Beautification?')}
      </h2>
      <div className="mt-4 space-y-3 text-sm leading-6 text-white/70">
        <p>
          {t(
            'Global beautification lets you use CSS code to change the appearance of the chat interface, including:'
          )}
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>{t('Message bubble colors and shapes')}</li>
          <li>{t('Narration box styles')}</li>
          <li>{t('Chat background')}</li>
          <li>{t('Fonts and spacing')}</li>
          <li>{t('Top and bottom areas')}</li>
        </ul>
        <p>
          <span className="font-semibold text-white">{t('Format rules:')}</span>{' '}
          {t(
            'All CSS must be wrapped in <style></style> tags, and every property must end with !important.'
          )}
        </p>
      </div>
    </div>
  )
}

function Page2() {
  const { t } = useI18n()

  const bgCode = `#tipsy-chat-container {
    background: #1a1a2e !important;
    padding: 10px !important;
    height: calc(100vh - 200px) !important;
    overflow-y: auto !important;
}`

  const bubbleCode = `#tipsy-char-message-bubble {
    background: #16213e !important;
    border: 1px solid #6366f1 !important;
    border-radius: 20px 20px 4px 20px !important;
    padding: 12px 15px !important;
    margin: 4px 0 !important;
    color: #e0e7ff !important;
    font-size: 14px !important;
    line-height: 1.7 !important;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.2) !important;
}`

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('Manual Replacement Tutorial (After Loading Template)')}
      </h2>
      <div className="mt-4 space-y-3 text-sm leading-6 text-white/70">
        <p>
          {t(
            'After clicking "Load Official Template", you can directly find and replace the annotation markers.'
          )}
        </p>
        <p className="font-semibold text-white">
          {t('Replace 1: Change chat background color')}
        </p>
        <p>
          {t(
            'Find /* === Chat Background Replace Start === */ and /* === Chat Background Replace End === */ in the code, replace the entire #tipsy-chat-container section between them with the code below for a dark background:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <CodeBlock code={bgCode} />
      </div>
      <div className="mt-4 space-y-3 text-sm leading-6 text-white/70">
        <p className="font-semibold text-white">
          {t('Replace 2: Change character message bubble style')}
        </p>
        <p>
          {t(
            'Find /* === Character Message Bubble Replace Start === */ and /* === Character Message Bubble Replace End === */, replace the content between them with:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <CodeBlock code={bubbleCode} />
      </div>
      <div className="mt-3 text-sm text-white/50">
        {t(
          'Effect: The chat background changes from vintage parchment to dark, and message bubbles change from brown to dark blue-purple. You can preview the effect on the right side after replacement.'
        )}
      </div>
    </div>
  )
}

function Page3() {
  const { t } = useI18n()

  // i18n-ignore: AI prompt kept in English for optimal LLM performance; users copy-paste this to AI
  const prompt = `I want to customize the chat interface style for my character page on a chat platform. The platform provides the following CSS selectors that I can use:

#tipsy-tagline-content — Tagline text area width
#tipsy-message-gap — Spacing between messages (adjust height)
#tipsy-char-msg-content — Spacing within character message container (adjust gap)
#tipsy-char-message-bubble — Character message bubble (color, border-radius, padding)
#tipsy-narration-box — Narration/scene description box style
#tipsy-user-msg-content — User message container width
#tipsy-user-message-bubble — User message bubble
#tipsy-chat-header — Chat page header (background, text style)
#tipsy-chat-container — Chat message list container
#tipsy-chat-bottom — Chat page bottom area
#tipsy-role-card-button — Character card button style
#tipsy-chat-input-area — Input box (size, background, border)
#tipsy-chat-send-button — Send button style
#tipsy-chat-top-gradient — Top gradient overlay
#tipsy-chat-bottom-overlay — Bottom gradient overlay
#tipsy-chat-background-overlay — Background overlay (opacity)
#tipsy-chat-background-image — Background image

Please write a CSS stylesheet for a vintage brown, parchment-style theme. Make it feel like an old military archive.

Important formatting rules:
- The entire CSS must be wrapped in <style></style> tags.
- Every CSS property must end with !important.

Example format:
<style>
#tipsy-char-message-bubble {
    background: #f8f0e0 !important;
    color: #2d2418 !important;
}
</style>`

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('AI Writes CSS for You (Official Prompt)')}
      </h2>
      <div className="mt-4 text-sm leading-6 text-white/70">
        <p>
          {t(
            'Copy the Prompt below to have AI generate global beautification CSS for you:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <ScrollableBlock text={prompt} />
      </div>
      <div className="mt-3 text-sm text-white/50">
        {t(
          'Tip: You can replace "vintage brown, parchment-style theme" with your desired style, like "dark neon cyberpunk theme" or "soft pastel macaron theme". The selector list is already included in the Prompt, AI will use it directly.'
        )}
      </div>
    </div>
  )
}

function Page4() {
  const { t } = useI18n()

  const tips = `1. ${t('layout_guide_tip_1')}
2. ${t('layout_guide_tip_2')}
3. ${t('layout_guide_tip_3')}
4. ${t('layout_guide_tip_4')}
5. ${t('layout_guide_tip_5')}
6. ${t('layout_guide_tip_6')}`

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('Advanced Requirements Reference (Official Tips)')}
      </h2>
      <div className="mt-4 text-sm leading-6 text-white/70">
        <p>
          {t(
            'After generating basic CSS, you can use the following requirements to further optimize:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <ScrollableBlock text={tips} />
      </div>
    </div>
  )
}

const PAGES = [Page1, Page2, Page3, Page4]

export default function LayoutGuideModal({
  open,
  onClose,
}: LayoutGuideModalProps) {
  return <GuideModalShell open={open} onClose={onClose} pages={PAGES} />
}
